import React, { useState } from "react";
import { X, Bell, Plus, Trash2 } from "lucide-react";

const defaultReminderOptions = [
	{ value: "0", label: "At time of event" },
	{ value: "5", label: "5 minutes before" },
	{ value: "10", label: "10 minutes before" },
	{ value: "15", label: "15 minutes before" },
	{ value: "30", label: "30 minutes before" },
	{ value: "60", label: "1 hour before" },
	{ value: "120", label: "2 hours before" },
	{ value: "1440", label: "1 day before" },
	{ value: "2880", label: "2 days before" },
	{ value: "10080", label: "1 week before" },
];

const RemindersModal = ({ reminders, onChange, onClose }) => {
	const [selectedReminder, setSelectedReminder] = useState("30");
	const [customMinutes, setCustomMinutes] = useState("");
	const [error, setError] = useState("");

	const handleAddReminder = () => {
		// For custom input
		let minutesToAdd = selectedReminder;
		if (customMinutes) {
			const parsed = parseInt(customMinutes, 10);
			if (isNaN(parsed) || parsed < 0) {
				setError("Please enter a valid number of minutes");
				return;
			}
			minutesToAdd = parsed.toString();
		}

		// Check for duplicates
		if (reminders.includes(minutesToAdd)) {
			setError("This reminder is already set");
			return;
		}

		// Add the reminder
		onChange(
			[...reminders, minutesToAdd].sort((a, b) => Number(a) - Number(b))
		);
		setCustomMinutes("");
		setError("");
	};

	const handleRemoveReminder = (reminderToRemove) => {
		onChange(reminders.filter((r) => r !== reminderToRemove));
	};

	const formatReminderTime = (minutes) => {
		const mins = parseInt(minutes, 10);
		if (mins === 0) return "At time of event";
		if (mins < 60) return `${mins} minutes before`;
		if (mins === 60) return "1 hour before";
		if (mins < 1440) return `${mins / 60} hours before`;
		if (mins === 1440) return "1 day before";
		if (mins < 10080) return `${mins / 1440} days before`;
		return `${mins / 10080} weeks before`;
	};

	return (
		<>
			{/* Modal Backdrop */}
			<div
				className="fixed inset-0 bg-black/20 dark:bg-black/40 z-[1000] backdrop-blur-sm"
				onClick={onClose}
			/>

			{/* Modal Content */}
			<div className="fixed z-[1001] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white dark:bg-gray-900 rounded-xl shadow-xl">
				{/* Header */}
				<div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-800">
					<h2 className="text-xl font-semibold text-gray-900 dark:text-white">
						Event reminders
					</h2>
					<button
						type="button"
						onClick={onClose}
						className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
					>
						<X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
					</button>
				</div>

				{/* Content */}
				<div className="p-4 space-y-4">
					{/* Add Reminder Section */}
					<div className="space-y-2">
						<div className="flex gap-2">
							<select
								value={selectedReminder}
								onChange={(e) => {
									setSelectedReminder(e.target.value);
									setCustomMinutes("");
									setError("");
								}}
								className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700
                  text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500
                  appearance-none cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
								style={{
									backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
									backgroundPosition: "right 0.5rem center",
									backgroundRepeat: "no-repeat",
									backgroundSize: "1.5em 1.5em",
									paddingRight: "2.5rem",
								}}
							>
								{defaultReminderOptions.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
								<option value="custom">Custom time before</option>
							</select>
							<button
								type="button"
								onClick={handleAddReminder}
								className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
							>
								Add
							</button>
						</div>

						{selectedReminder === "custom" && (
							<div className="flex gap-2 items-center">
								<input
									type="number"
									min="1"
									value={customMinutes}
									onChange={(e) => {
										setCustomMinutes(e.target.value);
										setError("");
									}}
									placeholder="Enter minutes"
									className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700
                    text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
								/>
								<span className="text-sm text-gray-600 dark:text-gray-300">
									minutes before
								</span>
							</div>
						)}

						{error && (
							<p className="text-sm text-red-500 dark:text-red-400">{error}</p>
						)}
					</div>

					{/* Current Reminders List */}
					{reminders.length > 0 && (
						<div className="space-y-2">
							<div className="text-sm font-medium text-gray-700 dark:text-gray-300">
								Current reminders
							</div>
							<div className="space-y-1">
								{reminders.map((reminder) => (
									<div
										key={reminder}
										className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg group"
									>
										<div className="flex items-center gap-2">
											<Bell className="w-4 h-4 text-gray-500 dark:text-gray-400" />
											<span className="text-sm text-gray-900 dark:text-gray-100">
												{formatReminderTime(reminder)}
											</span>
										</div>
										<button
											type="button"
											onClick={() => handleRemoveReminder(reminder)}
											className="p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity
                        hover:bg-gray-200 dark:hover:bg-gray-700
                        text-gray-500 dark:text-gray-400
                        hover:text-red-600 dark:hover:text-red-400"
										>
											<Trash2 className="w-4 h-4" />
										</button>
									</div>
								))}
							</div>
						</div>
					)}
				</div>

				{/* Footer */}
				<div className="flex justify-end gap-2 p-4 border-t border-gray-100 dark:border-gray-800">
					<button
						type="button"
						onClick={onClose}
						className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
					>
						Done
					</button>
				</div>
			</div>
		</>
	);
};

export default RemindersModal;
